import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loginUser,
  clearError,
  sendOtp,
  verifyOtp,
  resetPassword,
} from "../store/slices/authSlice";
import { validateEmail } from "../utils/validation";
import {
  getRememberMeData,
  setRememberMeData,
  clearRememberMeData,
} from "../utils/rememberMeStorage";

/**
 * Login page component with email/password authentication and forgot password flow
 */
const LoginPage = () => {
  const dispatch = useDispatch();
  const { loading, otpLoading, error } = useSelector((state) => state.auth);

  const getInitialFormData = () => {
    const saved = getRememberMeData();
    if (saved && saved.remember) {
      return {
        email: saved.email || "",
        password: saved.password || "",
        rememberMe: true,
        resetCode: "",
        newPassword: "",
        confirmPassword: "",
      };
    }
    return {
      email: "",
      password: "",
      rememberMe: false,
      resetCode: "",
      newPassword: "",
      confirmPassword: "",
    };
  };

  // Form states for different steps
  const [currentStep, setCurrentStep] = useState("login"); // 'login', 'reset', 'create'
  const [otpSent, setOtpSent] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name } = e.target;
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (error) dispatch(clearError());
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setFormErrors({ email: "Email is required" });
      return;
    }

    if (!validateEmail(formData.email)) {
      setFormErrors({ email: "Please enter a valid email" });
      return;
    }

    try {
      await dispatch(sendOtp({ email: formData.email })).unwrap();
      setOtpSent(true);
      setFormErrors({});
    } catch (error) {
      console.error("Failed to send OTP:", error.message);
      setFormErrors({ otpError: error.message });
      return;
    }
  };

  const validateLoginForm = () => {
    const errors = {};
    if (!formData.email) errors.email = "Email is required";
    else if (!validateEmail(formData.email))
      errors.email = "Please enter a valid email";
    if (!formData.password) errors.password = "Password is required";
    else if (formData.password.length < 6)
      errors.password = "Password must be at least 6 characters";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateResetForm = () => {
    const errors = {};
    if (!formData.resetCode) errors.resetCode = "Verification code is required";
    else if (formData.resetCode.length < 4)
      errors.resetCode = "Please enter a valid verification code";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCreatePasswordForm = () => {
    const errors = {};
    if (!formData.newPassword) errors.newPassword = "Password is required";
    else if (formData.newPassword.length < 6)
      errors.newPassword = "Password must be at least 6 characters";
    if (!formData.confirmPassword)
      errors.confirmPassword = "Please confirm your password";
    else if (formData.newPassword !== formData.confirmPassword)
      errors.confirmPassword = "Passwords do not match";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (!validateLoginForm()) return;
    dispatch(
      loginUser({ email: formData.email, password: formData.password })
    )
      .unwrap()
      .then(() => {
        if (formData.rememberMe) {
          setRememberMeData({
            email: formData.email,
            password: formData.password,
            remember: true,
          });
        } else {
          clearRememberMeData();
        }
      })
      .catch(() => {
        // Leave stored data as-is on login failure
      });
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!validateResetForm()) return;

    try {
      setCurrentStep("create");
    } catch (err) {
      console.error("OTP verification failed:", err);
    }
  };
  const handleVerifyOtpSubmit = async (e) => {
    try {
      let verifyOtpResponse = await dispatch(
        verifyOtp({ email: formData.email, otp: formData.resetCode })
      ).unwrap();

      if (verifyOtpResponse.success && verifyOtpResponse.passwordResetToken) {
        localStorage.setItem(
          "passwordResetToken",
          verifyOtpResponse.passwordResetToken
        );
      }
      setCurrentStep("create");
    } catch (err) {
      console.error("OTP verification failed:", err);
    }
  };

  const handleCreatePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validateCreatePasswordForm()) return;
    try {
      await dispatch(
        resetPassword({
          email: formData.email,
          newPassword: formData.newPassword,
          confirmNewPassword: formData.confirmPassword,
          passwordResetToken: localStorage.getItem("passwordResetToken"),
          otp: formData.resetCode,
        })
      ).unwrap();
      setOtpSent(false);
      if (error) dispatch(clearError());
      setCurrentStep("login");
    } catch (err) {
      console.error("Reset password failed:", err);
    }
    setFormData({ ...formData, newPassword: "", confirmPassword: "" });
    setFormErrors({});
  };

  const handleForgotPassword = () => {
    setCurrentStep("reset");
    setFormData((prev) => ({
      ...prev,
      resetCode: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setFormErrors({});
  };

  const handleBackToLogin = () => {
    setCurrentStep("login");
    setOtpSent(false);
    setFormData((prev) => ({
      ...prev,
      resetCode: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setFormErrors({});
    if (error) dispatch(clearError());
  };

  return (
    <div className="login-container">
      {/* Left Panel */}
      <div className="login-left-panel">
        {/* <div className="image-overlay"></div> */}
        <div className="login-branding">
          <h2 className="brand-subtitle">Next Gen Command & Control</h2>
          {/* <h1 className="brand-title">TimeMachine</h1> 
          <div className="brand-features">
            <div className="feature-item"><span className="feature-item-bullet">•</span>  Live 3D</div>
            <div className="feature-item"><span className="feature-item-bullet">•</span>  Software Connect</div>
            <div className="feature-item"><span className="feature-item-bullet">•</span>  IoT controls</div>
            <div className="feature-item"><span className="feature-item-bullet">•</span>  ML & AI</div>
            </div> 
            <p className="brand-tagline">Command Center Solution for Business</p>
            */}
        </div>
      </div>

      {/* Right Panel */}
      <div className="login-right-panel">
        <div className="login-form-container">
          <h1 className="form-title">
            {/* <span className="title-orange">Time Machine</span> Manager */}
            <img
              src="/images/omni_logo.png"
              alt="Time Machine"
              className="logo-image"
            />
          </h1>

          {/* Login Form */}
          {currentStep === "login" && (
            <>
              <form onSubmit={handleLoginSubmit} className="login-form">
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={formErrors.email ? "error" : ""}
                    placeholder="Email Address"
                    disabled={loading}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={formErrors.password ? "error" : ""}
                    placeholder="Password"
                    disabled={loading}
                  />
                  {formErrors.password && (
                    <span className="error-message">{formErrors.password}</span>
                  )}
                </div>

                <div className="form-group form-group-checkbox">
                  <label className="remember-me-label">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <span>Remember Me</span>
                  </label>
                </div>

                {error && <div className="error-alert">{error}</div>}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Login Now"}
                </button>
              </form>

              {/* Forgot Password Section - Commented Out */}
              {/* <div className="login-footer">
                <span className="forgot-text">Forgot password?</span>
                <a
                  href="#"
                  className="forgot-link"
                  onClick={handleForgotPassword}
                >
                  Click here
                </a>
              </div> */}
            </>
          )}

          {/* Reset Password Form - Commented Out */}
          {/* {currentStep === "reset" && (
            <>
              <h2 className="form-subtitle">Reset Password</h2>

              <form onSubmit={handleResetSubmit} className="login-form">
                {error && <div className="error-alert">{error}</div>}

                {!otpSent && (
                  <>
                    <div className="form-group">
                      <p className="form-description">
                        Please enter your registered email to receive a
                        verification code.
                      </p>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={formErrors.email ? "error" : ""}
                        placeholder="Enter Registered Email"
                        disabled={otpLoading || otpSent}
                      />
                      {formErrors.email && (
                        <span className="error-message">
                          {formErrors.email}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="login-button"
                      onClick={handleSendOtp}
                      disabled={otpLoading || !formData.email}
                    >
                      {otpLoading ? "Sending..." : "Send OTP"}
                    </button>
                  </>
                )}

                {otpSent && (
                  <>
                    <p className="otp-sent-message">
                      A verification code has been sent to your email.
                    </p>
                    <div className="form-group">
                      <input
                        type="text"
                        id="resetCode"
                        name="resetCode"
                        value={formData.resetCode}
                        onChange={handleChange}
                        className={formErrors.resetCode ? "error" : ""}
                        placeholder="Enter Verification Code"
                        disabled={otpLoading}
                      />
                      {formErrors.resetCode && (
                        <span className="error-message">
                          {formErrors.resetCode}
                        </span>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="login-button"
                      disabled={otpLoading || !formData.resetCode}
                      onClick={handleVerifyOtpSubmit}
                    >
                      {otpLoading ? "Verifying..." : "Next"}
                    </button>
                  </>
                )}
              </form>

              <div className="login-footer">
                <a href="#" className="forgot-link" onClick={handleBackToLogin}>
                  Back to Login
                </a>
              </div>
            </>
          )} */}

          {/* Create Password Form */}
          {currentStep === "create" && (
            <>
              <h2 className="form-subtitle">Create a strong password.</h2>

              <form
                onSubmit={handleCreatePasswordSubmit}
                className="login-form"
              >
                <div className="form-group">
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className={formErrors.newPassword ? "error" : ""}
                    placeholder="Create New Password"
                    disabled={loading}
                  />
                  {formErrors.newPassword && (
                    <span className="error-message">
                      {formErrors.newPassword}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={formErrors.confirmPassword ? "error" : ""}
                    placeholder="Confirm New Password"
                    disabled={loading}
                  />
                  {formErrors.confirmPassword && (
                    <span className="error-message">
                      {formErrors.confirmPassword}
                    </span>
                  )}
                </div>

                {error && <div className="error-alert">{error}</div>}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Password"}
                </button>
              </form>

              <div className="login-footer">
                <a href="#" className="forgot-link" onClick={handleBackToLogin}>
                  Back to Login
                </a>
              </div>
            </>
          )}

          <p className="login-copyright">
            © 2025 OomniEye. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
